import { PageProps } from "@/types/page";
import { usePage } from "@/utils/turboSafeInertia";
import { useEffect, useRef } from "react";

function useSocket<T>(
    channel: string,
    event: string,
    callback: (data: T) => void,
) {
    const cb = useRef(callback);
    cb.current = callback;
    useEffect(() => {
        window.Echo.private(channel).listen(event, cb.current);
        return () => {
            window.Echo.leaveChannel(channel);
        };
    }, []);
}

export function onLoggedOutWebsocketEvent<T>(
    token: string | null,
    eventName: string,
    callback: (data: T) => void,
) {
    const channel = `LoggedOutWebsocketChannel.${token}`;
    const cb = useRef(callback);
    cb.current = callback;
    useEffect(() => {
        if (!token) return;
        window.Echo.private(channel).listen(eventName, (event: T) => {
            cb.current(event);
        });
        return () => {
            window.Echo.leaveChannel(channel);
        };
    }, [token]);
}

type ChatEvent =
    | "ChatMessageCreated"
    | "ChatMessageUpdated"
    | "ChatMessageDeleted";

export function onChatEvent<T>(
    chatId: number,
    event: ChatEvent,
    callback: (data: T) => void,
) {
    const channel = `App.Models.Chat.${chatId}`;
    return useSocket(channel, event, callback);
}

export const ALL_NOTIFICATIONS = "ALL_NOTIFICATIONS";

export function onUserNotification<T>(
    notificationName: string,
    callback: (data: T) => void,
) {
    const { user, twoFactorPassed } = usePage<PageProps>().props;
    const type =
        user?.type === "client"
            ? "Client"
            : user?.type === "coach"
              ? "Coach"
              : "Unauthenticated";
    const channel = `App.Models.${type}.${user?.id ?? "Unauthenticated"}`;
    const cb = useRef(callback);
    cb.current = callback;

    useEffect(() => {
        if (!user || !twoFactorPassed) return;
        window.Echo.private(channel).notification(
            (notification: { type: string; id: string }) => {
                if (
                    notification.type === notificationName ||
                    notificationName === ALL_NOTIFICATIONS
                ) {
                    cb.current(notification as T);
                }
            },
        );
        return () => {
            window.Echo.leaveChannel(channel);
        };
    }, [user?.type, twoFactorPassed]);
}

export function onUserEvent<T>(eventName: string, callback: (data: T) => void) {
    const { user, twoFactorPassed } = usePage<PageProps>().props;
    const type =
        user?.type === "client"
            ? "Client"
            : user?.type === "coach"
              ? "Coach"
              : "Unauthenticated";
    const channel = `App.Models.${type}.${user?.id ?? "Unauthenticated"}`;
    const cb = useRef(callback);
    cb.current = callback;

    useEffect(() => {
        if (!user || !twoFactorPassed) return;
        window.Echo.private(channel).listen(
            eventName,
            (event: { type: string; id: string }) => {
                cb.current(event as T);
            },
        );
        return () => {
            window.Echo.private(channel).stopListening(eventName);
        };
    }, [user?.type, twoFactorPassed]);
}

export function onScheduleChange({
    clientId,
    callback,
}: {
    clientId?: number;
    callback: () => void;
}) {
    const { user, twoFactorPassed } = usePage<PageProps>().props;
    const cb = useRef(callback);
    cb.current = callback;

    const handleNotification = (notification: {
        type: string;
        id: string;
        client_id?: number;
    }) => {
        if (
            user?.type === "coach" &&
            clientId !== undefined &&
            notification.client_id !== clientId
        ) {
            return;
        }
        const scheduleNotificationTypes = [
            "App\\Notifications\\Appointments\\AppointmentCreatedByClientNotificationForClient",
            "App\\Notifications\\Appointments\\AppointmentCreatedByCoachNotificationForClient",
            "App\\Notifications\\Appointments\\AppointmentDeletedByClientNotificationForClient",
            "App\\Notifications\\Appointments\\AppointmentDeletedByCoachNotificationForClient",
            "App\\Notifications\\Appointments\\AppointmentCreatedByClientNotificationForCoach",
            "App\\Notifications\\Appointments\\AppointmentCreatedByCoachNotificationForCoach",
            "App\\Notifications\\Appointments\\AppointmentDeletedByClientNotificationForCoach",
            "App\\Notifications\\Appointments\\AppointmentDeletedByCoachNotificationForCoach",
            "ClientCoachScheduleMultipleChangedEvent",
            "AppointmentRescheduled",
        ];
        if (scheduleNotificationTypes.includes(notification.type)) {
            cb.current();
        }
    };

    useEffect(() => {
        if (!user || !twoFactorPassed) return;
        const channel = `App.Models.${user.type === "client" ? "Client" : "Coach"}.${user.id}`;
        window.Echo.private(channel).notification(handleNotification);
        return () => {
            window.Echo.leaveChannel(channel);
        };
    }, [user?.type, twoFactorPassed]);
}
